/**
 * Data Processing Hooks for ProcessedDataChart
 * Handles data transformation, downsampling, and display data preparation
 */

import { useMemo } from 'react';
import { DataPoint, ProcessedChartData, DisplayData } from '../types';
import { lttbDownsample } from '../utils';
import { DEFAULT_MAX_POINTS } from '../constants';

/**
 * Process raw data into timestamps and values arrays
 */
export function useProcessedData(
  data: DataPoint[],
  seriesName: string
): ProcessedChartData {
  return useMemo(() => {
    if (!data || data.length === 0 || !seriesName) {
      return { timestamps: [], values: [], validCount: 0, yMin: 0, yMax: 0 };
    }

    const timestamps: number[] = [];
    const values: (number | null)[] = [];
    let yMin = Infinity;
    let yMax = -Infinity;
    let validCount = 0;

    for (let i = 0; i < data.length; i++) {
      const d = data[i];
      const raw = d[seriesName];

      // Parse UTC - backend sends milliseconds as number
      let ts: number;
      if (typeof d.UTC === "number") {
        ts = d.UTC / 1000; // ms → seconds for uPlot
      } else {
        ts = new Date(d.UTC as string).getTime() / 1000;
      }

      if (!Number.isFinite(ts)) continue;

      timestamps.push(ts);

      // Handle value - can be number, string, null, or "None"
      let v: number | null = null;
      if (raw === null || raw === undefined || raw === "None" || raw === "NaN") {
        v = null;
      } else if (typeof raw === "number") {
        v = Number.isFinite(raw) ? raw : null;
      } else if (typeof raw === "string") {
        const parsed = Number(raw);
        v = Number.isFinite(parsed) ? parsed : null;
      }

      values.push(v);

      if (v !== null) {
        validCount++;
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }

    // Handle edge case where all values are null
    if (yMin === Infinity) yMin = 0;
    if (yMax === -Infinity) yMax = 1;

    return { timestamps, values, validCount, yMin, yMax };
  }, [data, seriesName]);
}

/**
 * Apply LTTB downsampling for display
 */
export function useDisplayData(
  processedData: ProcessedChartData,
  showAll: boolean
): DisplayData {
  return useMemo(() => {
    const { timestamps, values } = processedData;

    if (timestamps.length === 0) {
      return { timestamps: [], values: [] };
    }

    // If showAllData is enabled, return all points without downsampling
    if (showAll) {
      return {
        timestamps: [...timestamps],
        values: [...values]
      };
    }

    // Target points for smooth zooming
    const targetPoints = Math.min(timestamps.length, DEFAULT_MAX_POINTS);

    if (timestamps.length <= targetPoints) {
      return { timestamps: [...timestamps], values: [...values] };
    }

    return lttbDownsample(timestamps, values, targetPoints);
  }, [processedData, showAll]);
}
